/**
 * Client-side branching engine.
 * Evaluates question visibility based on branching rules and current answers.
 * Pure logic — no React, no side effects.
 */

export interface BranchingRule {
    id: string;
    sourceQuestionId: string;
    sourceAnswerValue: string;
    targetQuestionId: string;
    logicOperator:
        | "equals"
        | "includes"
        | "not_equals"
        | "greater_than"
        | "less_than"
        | "out_of_range"
        | "any_of";
}

export interface QuestionWithRules {
    id: string;
    branchingRules: BranchingRule[];
}

/** Map of questionId → answer value (string or JSON-stringified array for multi-select) */
export type AnswerMap = Record<string, string>;

/**
 * Evaluate a single branching rule against current answers.
 */
function evaluateRule(rule: BranchingRule, answers: AnswerMap): boolean {
    const answer = answers[rule.sourceQuestionId];
    if (answer === undefined || answer === null) {
        return false;
    }

    switch (rule.logicOperator) {
        case "equals":
            return answer.toLowerCase() === rule.sourceAnswerValue.toLowerCase();
        case "not_equals":
            return answer.toLowerCase() !== rule.sourceAnswerValue.toLowerCase();
        case "includes": {
            // For multi-select: answer is JSON array string, check if value is included
            try {
                const parsed = JSON.parse(answer);
                if (Array.isArray(parsed)) {
                    const target = rule.sourceAnswerValue.toLowerCase();
                    return parsed.some((v: string) => String(v).toLowerCase() === target);
                }
            } catch {
                // Not JSON — fall back to string equals
            }
            return answer.toLowerCase() === rule.sourceAnswerValue.toLowerCase();
        }
        case "greater_than": {
            const n = parseFloat(answer);
            const threshold = parseFloat(rule.sourceAnswerValue);
            return Number.isFinite(n) && Number.isFinite(threshold) && n > threshold;
        }
        case "less_than": {
            const n = parseFloat(answer);
            const threshold = parseFloat(rule.sourceAnswerValue);
            return Number.isFinite(n) && Number.isFinite(threshold) && n < threshold;
        }
        case "out_of_range": {
            // sourceAnswerValue is "<low>,<high>" — show when answer < low OR > high
            const n = parseFloat(answer);
            if (!Number.isFinite(n)) {
                return false;
            }
            const [lowStr, highStr] = rule.sourceAnswerValue.split(",");
            const low = parseFloat(lowStr);
            const high = parseFloat(highStr);
            if (!Number.isFinite(low) || !Number.isFinite(high)) {
                return false;
            }
            return n < low || n > high;
        }
        case "any_of": {
            // sourceAnswerValue is a comma-separated list of candidate values
            // (e.g. "Lethargic,Comatose" for a radio source, or
            // "Retention,Anuria,Oliguria" for a multi-select source).
            // Behavior depends on the answer shape:
            //   - Multi-select answer (JSON array): matches if the array
            //     contains ANY candidate.
            //   - Single-value answer (string): matches if the answer equals
            //     ANY candidate.
            // Strict superset of `equals` (radio source) and `includes`
            // (multi-select source) when the candidate list has one entry.
            const candidates = rule.sourceAnswerValue
                .split(",")
                .map((c) => c.trim().toLowerCase())
                .filter((c) => c.length > 0);
            if (candidates.length === 0) {
                return false;
            }
            try {
                const parsed = JSON.parse(answer);
                if (Array.isArray(parsed)) {
                    return parsed.some((v: string) => candidates.includes(String(v).toLowerCase()));
                }
            } catch {
                // Not JSON — fall through to single-value match
            }
            return candidates.includes(answer.toLowerCase());
        }
        default:
            return false;
    }
}

/**
 * Returns the set of visible question IDs given questions, their branching rules, and current answers.
 * A question is visible if:
 *   - It has no branching rules (always visible), OR
 *   - ALL its branching rules are satisfied (AND logic), AND
 *   - Its source question (parent) is also visible (cascading hide)
 */
export function evaluateVisibility(
    questions: QuestionWithRules[],
    answers: AnswerMap
): Set<string> {
    // Build lookup: questionId → question
    const questionMap = new Map<string, QuestionWithRules>();
    for (const q of questions) {
        questionMap.set(q.id, q);
    }

    // Cache visibility results to avoid redundant computation
    const cache = new Map<string, boolean>();

    function isVisible(questionId: string): boolean {
        if (cache.has(questionId)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return cache.get(questionId)!;
        }

        const question = questionMap.get(questionId);
        if (!question) {
            cache.set(questionId, false);
            return false;
        }

        // No rules → always visible
        if (question.branchingRules.length === 0) {
            cache.set(questionId, true);
            return true;
        }

        // All rules must pass (AND)
        for (const rule of question.branchingRules) {
            if (!evaluateRule(rule, answers)) {
                cache.set(questionId, false);
                return false;
            }
            // Parent must also be visible (cascading hide)
            if (!isVisible(rule.sourceQuestionId)) {
                cache.set(questionId, false);
                return false;
            }
        }

        cache.set(questionId, true);
        return true;
    }

    const visible = new Set<string>();
    for (const q of questions) {
        if (isVisible(q.id)) {
            visible.add(q.id);
        }
    }
    return visible;
}

/**
 * Compute the branching depth (indentation level) for each question.
 * Depth 0 = no branching condition (top-level).
 * Depth N = N levels deep in the condition chain.
 */
export function computeDepths(questions: QuestionWithRules[]): Map<string, number> {
    const questionMap = new Map<string, QuestionWithRules>();
    for (const q of questions) {
        questionMap.set(q.id, q);
    }

    const cache = new Map<string, number>();

    function getDepth(questionId: string): number {
        if (cache.has(questionId)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return cache.get(questionId)!;
        }

        const question = questionMap.get(questionId);
        if (!question || question.branchingRules.length === 0) {
            cache.set(questionId, 0);
            return 0;
        }

        // Depth = max parent depth + 1
        let maxParentDepth = 0;
        for (const rule of question.branchingRules) {
            const parentDepth = getDepth(rule.sourceQuestionId);
            maxParentDepth = Math.max(maxParentDepth, parentDepth);
        }

        const depth = maxParentDepth + 1;
        cache.set(questionId, depth);
        return depth;
    }

    const depths = new Map<string, number>();
    for (const q of questions) {
        depths.set(q.id, getDepth(q.id));
    }
    return depths;
}

/**
 * Order branched questions under their trigger.
 *
 * The form data parks branched follow-up questions at high order_index values
 * (the ≥ 1000 "append at end" convention), so naive ordering by order_index
 * drops them at the bottom of the section instead of right after the question
 * that triggered them. This pure function takes a list of root questions
 * already in order_index order and re-emits the ids so each branched question
 * appears immediately after its trigger (the rule's sourceQuestionId),
 * depth-first. Sibling order is preserved from the input (so order_index
 * still decides the order among a parent's own follow-ups — it just no longer
 * decides where the cluster sits).
 *
 * No DB change, no renumbering, no schema change. Visibility is the caller's
 * concern — pass in the already-visible set so hidden branches don't
 * influence placement.
 */
export function computeRenderOrder(questions: QuestionWithRules[]): string[] {
    const byId = new Map<string, QuestionWithRules>();
    for (const q of questions) {
        byId.set(q.id, q);
    }

    // For each branched question, its placement parent is the first rule
    // whose sourceQuestionId is also present in this set (and not the
    // question itself). If no such rule exists, it's a root in this list.
    const childrenOf = new Map<string, QuestionWithRules[]>();
    const roots: QuestionWithRules[] = [];
    for (const q of questions) {
        const parentId = q.branchingRules
            .map((r) => r.sourceQuestionId)
            .find((src) => src !== q.id && byId.has(src));
        if (parentId != null) {
            const list = childrenOf.get(parentId) ?? [];
            list.push(q);
            childrenOf.set(parentId, list);
        } else {
            roots.push(q);
        }
    }

    const result: string[] = [];
    const seen = new Set<string>();
    function emit(q: QuestionWithRules): void {
        if (seen.has(q.id)) {
            return;
        }
        seen.add(q.id);
        result.push(q.id);
        for (const child of childrenOf.get(q.id) ?? []) {
            emit(child);
        }
    }
    for (const r of roots) {
        emit(r);
    }
    // Safety: anything whose declared parent isn't in this set (cycles or
    // cross-section refs) gets appended in input order so nothing disappears.
    for (const q of questions) {
        if (!seen.has(q.id)) {
            seen.add(q.id);
            result.push(q.id);
        }
    }
    return result;
}

/**
 * Which stored answers would a pending change orphan?
 *
 * Closing a branch does not clear what is underneath it. An RN who answers
 * "Yes", fills in the follow-ups, then switches the parent to "No" leaves those
 * answers in `visit_answers`: gone from her screen, still read by the PDF, the
 * branched-summary LLM and the correction bot. A production scan found this on
 * ~21% of visits in a 30-day window, spread across ~200 different questions and
 * 70% of active nurses — ordinary behaviour, not a few careless users.
 *
 * This returns the ids of questions that are visible and answered NOW but would
 * be hidden once `questionId` becomes `newValue`, so the caller can confirm the
 * deletion with the nurse before it happens rather than silently keeping data
 * she believes she removed.
 *
 * Uses `evaluateVisibility` for both states, so the whole subtree is covered:
 * a grandchild whose own rule still matches is still orphaned when its parent
 * goes. Checking one level down is exactly the mistake that let a walker
 * survive a patient being marked as not walking.
 *
 * An empty string or "[]" counts as no answer — the same emptiness test
 * `validateRequired` uses — so there is nothing to warn about.
 */
export function answersOrphanedBy(
    questions: QuestionWithRules[],
    answers: AnswerMap,
    questionId: string,
    newValue: string
): string[] {
    const before = evaluateVisibility(questions, answers);
    const after = evaluateVisibility(questions, { ...answers, [questionId]: newValue });

    const hasAnswer = (id: string): boolean => {
        const v = answers[id];
        return v != null && v.trim() !== "" && v.trim() !== "[]";
    };

    return questions
        .map((q) => q.id)
        .filter((id) => id !== questionId && before.has(id) && !after.has(id) && hasAnswer(id));
}
